import {
  FC,
  ReactElement,
  useCallback,
  useEffect,
  useMemo,
  useState,
} from "react";
import Stepper, { Orientation } from "@mui/material/Stepper";
import {
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Step,
  StepButton,
  StepContent,
} from "@mui/material";
import { useTranslation } from "react-i18next";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { useMediaQuery } from "react-responsive";
import { ScreenSize } from "@constants/screen-size";
import { useSelector } from "react-redux";
import { RootState } from "@store/store";
import AccountCircleIcon from "@mui/icons-material/AccountCircle";
import GroupIcon from "@mui/icons-material/Group";
import AddPhotoAlternateOutlinedIcon from "@mui/icons-material/AddPhotoAlternateOutlined";
import PortraitIcon from "@mui/icons-material/Portrait";
import { Person, PersonPin } from "@mui/icons-material";
import { useGetPatientQuery } from "@store/patientApi";
import {
  isAddPatientFlow,
  withOnboardingFlow,
} from "@components/Onboarding/onboardingFlow";

export interface StepDef {
  name: string;
  completed?: boolean;
  path: string;
  exact?: boolean;
  disabled?: boolean;
  icon?: ReactElement;
  children?: Array<StepDef>;
}

const DisabledStep = (stepDef: StepDef, disabled: boolean) => {
  return {
    ...stepDef,
    disabled,
  };
};

const Steps = {
  accountInfo: {
    name: "onboarding.stepper.accountInfo",
    path: "/onboarding",
    exact: true,
  },
  profileInfo: {
    name: "onboarding.stepper.profileInfo",
    path: "/onboarding/profile",
    icon: <AccountCircleIcon />,
  },
  familyInfo: {
    name: "onboarding.stepper.addFamilyMembers",
    path: "/onboarding/family-info",
    icon: <GroupIcon />,
  },
  persons: {
    name: "onboarding.stepper.addPersons",
    path: "/onboarding/profile",
  },
  gallery: {
    name: "onboarding.stepper.gallery",
    path: "/onboarding/pictures",
  },
  pictures: {
    name: "onboarding.stepper.addPhotos",
    path: "/onboarding/pictures",
    icon: <AddPhotoAlternateOutlinedIcon />,
  },
  characters: {
    name: "onboarding.stepper.tagPeople",
    path: "/onboarding/characters",
    icon: <PortraitIcon />,
  },
  companion: {
    name: "onboarding.stepper.companion",
    path: "/onboarding/companion/patient",
  },
  companionForPatient: {
    name: "onboarding.stepper.forPatient",
    path: "/onboarding/companion/patient",
    icon: <PersonPin />,
  },
  companionForYou: {
    name: "onboarding.stepper.forYou",
    path: "/onboarding/companion/caregiver",
    icon: <Person />,
  },
  companionForPatientStandalone: {
    name: "onboarding.stepper.forPatientStandalone",
    path: "/onboarding/companion/patient",
    icon: <PersonPin />,
  },
  companionForYouStandalone: {
    name: "onboarding.stepper.forYouStandalone",
    path: "/onboarding/companion/caregiver",
    icon: <Person />,
  },
} as const;

const getSteps = (
  orientation: Orientation,
  accountId: string | null,
  patientId: string | null,
  isPatientOnlyFlow: boolean,
): StepDef[] => {
  if (isPatientOnlyFlow && orientation === "horizontal") {
    return [
      DisabledStep(Steps.profileInfo, false),
      DisabledStep(Steps.familyInfo, !patientId),
      DisabledStep(Steps.pictures, !patientId),
      DisabledStep(Steps.characters, !patientId),
      DisabledStep(Steps.companionForPatientStandalone, !patientId),
    ];
  }

  if (isPatientOnlyFlow) {
    return [
      DisabledStep(
        {
          ...Steps.persons,
          children: [Steps.profileInfo, DisabledStep(Steps.familyInfo, !patientId)],
        },
        false,
      ),
      DisabledStep(
        {
          ...Steps.gallery,
          children: [Steps.pictures, Steps.characters],
        },
        !patientId,
      ),
      DisabledStep(
        {
          ...Steps.companion,
          children: [Steps.companionForPatient],
        },
        !patientId,
      ),
    ];
  }

  if (orientation === "horizontal") {
    return [
      DisabledStep(Steps.accountInfo, false),
      DisabledStep(Steps.profileInfo, !accountId),
      DisabledStep(Steps.familyInfo, !accountId || !patientId),
      DisabledStep(Steps.persons, !accountId || !patientId),
      DisabledStep(Steps.pictures, !accountId || !patientId),
      DisabledStep(Steps.characters, !accountId || !patientId),
      DisabledStep(
        Steps.companionForPatientStandalone,
        !accountId || !patientId,
      ),
      DisabledStep(
        Steps.companionForYouStandalone,
        !accountId || !patientId,
      ),
    ];
  }

  return [
    DisabledStep(Steps.accountInfo, false),
    DisabledStep(
      {
        ...Steps.persons,
        children: [
          Steps.profileInfo,
          DisabledStep(Steps.familyInfo, !patientId),
        ],
      },
      !accountId,
    ),
    DisabledStep(
      {
        ...Steps.gallery,
        children: [Steps.pictures, Steps.characters],
      },
      !accountId || !patientId,
    ),
    DisabledStep(
      {
        ...Steps.companion,
        children: [Steps.companionForPatient, Steps.companionForYou],
      },
      !accountId || !patientId,
    ),
  ];
};

const isActiveStep = (
  locationPath: string,
  { path, exact }: StepDef,
): boolean => {
  locationPath = locationPath.replace(
    /\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/,
    "",
  );

  return exact ? locationPath === path : locationPath.startsWith(path);
};

const OnboardingStepper: FC = () => {
  const { t } = useTranslation();
  const location = useLocation();
  const accountId = useSelector((state: RootState) => state.account.accountId);
  const patientId = useSelector(
    (state: RootState) => state.home.selectedPatientId,
  );
  const isPatientOnlyFlow = isAddPatientFlow(location.search);
  const pathPatientIdMatch = location.pathname.match(
    /\/([0-9a-fA-F]{8}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{4}-[0-9a-fA-F]{12})/,
  );
  const pathPatientId = pathPatientIdMatch?.[1] || null;
  const effectivePatientId = isPatientOnlyFlow
    ? pathPatientId
    : pathPatientId || patientId;
  const isSmScreen = useMediaQuery({
    query: `(max-width: ${ScreenSize.md}px)`,
  });
  const { data: patient } = useGetPatientQuery(effectivePatientId!, {
    skip: !effectivePatientId,
  });

  const navigate = useNavigate();
  const steps = useMemo(
    () =>
      getSteps(
        isSmScreen ? "horizontal" : "vertical",
        accountId,
        effectivePatientId,
        isPatientOnlyFlow,
      ),
    [isSmScreen, accountId, effectivePatientId, isPatientOnlyFlow],
  );
  const [activeStep, setActiveStep] = useState<StepDef>(steps[0]);

  useEffect(() => {
    const step = steps.find((step) => {
      return (
        isActiveStep(location.pathname, step) ||
        step.children?.find((child) => isActiveStep(location.pathname, child))
      );
    });
    if (step) {
      setActiveStep(step);
    }
  }, [location, steps]);

  const handleStep = useCallback(
    (step: StepDef) => {
      setActiveStep(step);
      navigate(withOnboardingFlow(step.path, location.search));
    },
    [navigate, location.search],
  );

  const activeStepIndex = useMemo(
    () => steps.indexOf(activeStep),
    [steps, activeStep],
  );

  const isActive = useCallback(
    (step: StepDef) => isActiveStep(location.pathname, step),
    [location],
  );

  return (
    <Stepper
      nonLinear
      orientation={isSmScreen ? "horizontal" : "vertical"}
      activeStep={activeStepIndex}
    >
      {steps.map((step, index) => (
        <Step key={index} completed={step.completed}>
          <StepButton onClick={() => handleStep(step)} disabled={step.disabled}>
            {t(step.name, { person: patient?.firstName })}
          </StepButton>
          {step.children?.length && !isSmScreen ? (
            <StepContent>
              <List>
                {step.children.map((step, index) => {
                  const { icon, name, path, disabled } = step;
                  const selected = isActive(step);
                  return (
                    <ListItem disableGutters={true} key={index}>
                      <ListItemButton
                        selected={selected}
                        component={Link}
                        disabled={disabled}
                        to={withOnboardingFlow(path, location.search)}
                        sx={{
                          borderRadius: "4px",
                          "&.Mui-selected, &.Mui-selected:hover": {
                            backgroundColor: "primary.main",
                            color: "primary.contrastText",
                          },
                          "&.Mui-selected.Mui-focusVisible": {
                            backgroundColor: "primary.dark",
                          },
                          svg: {
                            color: selected
                              ? "primary.contrastText"
                              : "grey.800",
                          },
                        }}
                      >
                        <ListItemIcon
                          sx={{
                            minWidth: "32px",
                          }}
                        >
                          {icon}
                        </ListItemIcon>
                        <ListItemText
                          primary={t(name, { person: patient?.firstName })}
                        />
                      </ListItemButton>
                    </ListItem>
                  );
                })}
              </List>
            </StepContent>
          ) : (
            <></>
          )}
        </Step>
      ))}
    </Stepper>
  );
};

export default OnboardingStepper;
